import { createContext, useContext } from 'react';
import { SWRInfiniteKeyedMutator } from 'swr/infinite';

import { OrbitMutationFailure, OrbitMutationOutcome } from 'api/orbit';
import {
  ScreenCardInView,
  ScreenFilterChip,
  ScreenFilterSignature
} from 'containers/Intelligence/filters/screenFilterSignature';

import { OrbitEntryContext, OrbitListResponse, OrbitTurn } from './types';

export interface OrbitContextValue {
  open: boolean;
  /**
   * Mounts a non-panel Orbit surface (the full page): while one is attached the transcript
   * loads as it does with the panel open. Returns the detach.
   */
  attachPageSurface: () => () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  /**
   * Returns focus to whatever had it when the panel was opened. The provider captures the opener at
   * `openSidebar`/`toggleSidebar` time, because by the time the panel's own effect runs the launcher
   * that was clicked has already unmounted and `document.activeElement` is `<body>`.
   */
  restoreOpenerFocus: () => void;
  sessionId: string | null;
  /**
   * Id of the turn most recently created by `ask()`. It stays set after the turn reaches a terminal
   * state so the answer can render before the session list has revalidated; the list will contain
   * the same turn once it does, so consumers de-duplicate by `id`.
   */
  activeTurnId: number | null;
  activeTurn: OrbitTurn | undefined;
  /** Axios error from the active-turn poll; the answer for that turn cannot be shown while it is set. */
  activeTurnError: unknown;
  /** The question being asked, from submit until the turn is terminal or the POST failed. */
  pendingQuestion: string | null;
  isAsking: boolean;
  askError: OrbitMutationFailure | null;
  /**
   * The unsent question. It lives here rather than in `OrbitComposer` because the composer is
   * unmounted by two ordinary interactions — opening the conversation list and closing the panel —
   * and a local `useState` would discard the user's typing both times.
   */
  draft: string;
  setDraft: (draft: string) => void;
  /** Live context, derived from the route the user is on right now. */
  entryContext: OrbitEntryContext;
  /**
   * Context the running investigation belongs to: what was persisted for the session, or the oldest
   * loaded turn's for one resumed from the list. `null` before a session has any turn.
   */
  sessionContext: OrbitEntryContext | null;
  /**
   * What the running investigation is ABOUT, derived from its opening turn and persisted with the
   * session id. The launcher's label card names it while the panel is closed, which is the one
   * moment the transcript it comes from is not being fetched. `undefined` before a session's first
   * turn has loaded, and for a session whose opening turn carries neither a metric nor a question.
   */
  sessionSubject: string | undefined;
  /**
   * Live filter state of the screen the user is on — Customer Insights or Customer 360 — or `null`
   * on every screen that publishes none. Client-side only: the PLA-8839 context object has no
   * filter field and `ask()` never sends one. Any change to it raises the conflict card the way a
   * route change does (PLA-8844 D2), and the context section draws one chip per entry.
   */
  entryFilters: ScreenFilterSignature | null;
  /** Human summary of the filters the running investigation was started under; `''` when none. */
  sessionFilters: string;
  /**
   * The chips and the cards the running investigation was started under, drawn as the context row
   * once the conversation is pinned: from that point `ask()` sends `sessionContext`, so a row drawn
   * from the live screen would name a context the next answer is not about (PLA-9563 §1c).
   */
  sessionChips: ScreenFilterChip[];
  sessionCards: ScreenCardInView[];
  /** Keep Current Investigation was chosen: every later question is sent under `sessionContext`. */
  contextPinned: boolean;
  /**
   * Every question already put to Orbit in this conversation, lower-cased and stripped of trailing
   * punctuation (`orbitQuestionKey`). The contextual starters are a static catalogue, so this is
   * what stops the suggestion rows re-offering a question that has just been answered.
   */
  askedQuestions: ReadonlySet<string>;
  /** The live route no longer matches `sessionContext` and the user has not answered the card yet. */
  contextConflict: boolean;
  /** Context the investigation was carried into, drawn as the handoff card until the session ends. */
  handoffContext: OrbitEntryContext | null;
  /**
   * Id of the newest turn at the moment the handoff happened, so the card sits between the turn
   * that preceded the move and the ones asked after it (`5017:8217` draws turns below it). `null`
   * when the handoff preceded every loaded turn.
   */
  handoffAfterTurnId: number | null;
  /** Continue with New Investigation: `sessionContext` becomes the live one and the pin is released. */
  adoptLiveContext: () => void;
  /** Keep Current Investigation: `ask()` keeps sending `sessionContext` instead of the live route's. */
  pinSessionContext: () => void;
  /** Turns of the current session, newest first, accumulated across pages. Fetched only while open. */
  turns: OrbitTurn[];
  turnsLoading: boolean;
  /** A page older than the loaded ones is in flight. */
  turnsLoadingMore: boolean;
  /** The session has turns older than the ones loaded; `loadOlderTurns` fetches the next page. */
  hasOlderTurns: boolean;
  loadOlderTurns: () => void;
  /** Axios error from the last turns fetch. */
  turnsError: unknown;
  /**
   * True after a stored session id resolved to an empty transcript: the conversation was cleared or
   * expired server-side, the id has been dropped, and the UI shows the "conversation ended" notice
   * until the next `ask()` or `newChat()`.
   */
  sessionStale: boolean;
  ask: (question: string) => Promise<OrbitMutationOutcome<OrbitTurn>>;
  /**
   * Polls a turn the server created for an action (a pick or a confirm) as if it had just been
   * asked. A finished turn is never re-polled, so this is how the panel sees the action's result.
   */
  followTurn: (turn: OrbitTurn) => void;
  newChat: () => void;
  /** Switches to an existing conversation from the list; the next `ask()` continues it. */
  resumeSession: (sessionId: string) => void;
  clearConversation: () => Promise<OrbitMutationOutcome<null>>;
  /** The bound infinite mutator for the session transcript: no args revalidates every loaded page. */
  revalidateTurns: SWRInfiniteKeyedMutator<OrbitListResponse[]>;
}

// Kept in its own module so consumers import a leaf with no provider dependencies.
const OrbitContext = createContext<OrbitContextValue | null>(null);

export const useOrbitContext = (): OrbitContextValue => {
  const value = useContext(OrbitContext);

  if (value === null) {
    throw new Error('useOrbitContext must be used within an OrbitProvider');
  }

  return value;
};

export default OrbitContext;
